<script setup lang="ts">
import { ClipboardPlus } from 'lucide-vue-next';
import { toast } from 'vue-sonner';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import TaskAssigneePicker from '@/components/tasks/TaskAssigneePicker.vue';
import TaskMilestoneSelect from '@/components/tasks/TaskMilestoneSelect.vue';
import TagInput from '@/components/entity/TagInput.vue';
import { buildTaskAssigneePayload, resolveTaskAssigneeIds } from '~/lib/taskAssignees';
import { buildQuickTaskPayload, canQuickCreateTask, createTaskQuickDraft, type TaskQuickCreateDraft } from '~/lib/taskCreation';
import { getTaskScheduleInputValues, normalizeTaskScheduleDates } from '~/lib/taskSchedule';
import type { ProjectStage, Task, TaskPriority } from '~/types/models';

const props = withDefaults(defineProps<{
    open: boolean;
    initialDraft?: Partial<Task> | null;
    statusOptions?: string[];
    assigneeOptions?: Array<{ id: string; label: string; avatarURL?: string }>;
    priorityOptions?: Array<{ value: TaskPriority; label: string; color?: string }>;
    projectOptions?: Array<{ id: string; name: string; clientId?: string; stages?: ProjectStage[] }>;
    clientOptions?: Array<{ id: string; name: string }>;
}>(), {
    initialDraft: null,
    statusOptions: () => ['Todo', 'In Progress', 'Blocked', 'Done'],
    assigneeOptions: () => [],
    priorityOptions: () => [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'critical', label: 'Critical' },
    ],
    projectOptions: () => [],
    clientOptions: () => [],
});

const emit = defineEmits<{
    'update:open': [value: boolean];
    created: [task: Task];
}>();

const taskStore = useTaskStore();
const entityTagsStore = useEntityTagsStore();
const authStore = useAuthStore();
const userStore = useUserStore();
const saving = ref(false);
const draft = ref<TaskQuickCreateDraft>(createDraft());
const selectedTags = ref<string[]>([]);

const canCreate = computed(() => canQuickCreateTask(draft.value) && !saving.value);
const projectOptionMap = computed(() => new Map(props.projectOptions.map(project => [project.id, project])));
const projectMenuOptions = computed(() => props.projectOptions.map(project => ({ value: project.id, label: project.name })));
const clientMenuOptions = computed(() => props.clientOptions.map(client => ({ value: client.id, label: client.name })));
const statusMenuOptions = computed(() => props.statusOptions.map(status => ({ value: status, label: status })));
const priorityMenuOptions = computed(() => props.priorityOptions.map(priority => ({ value: priority.value, label: priority.label })));
const tagSuggestions = computed(() => entityTagsStore.allTags);
const selectedAssigneeIds = computed({
    get: () => resolveTaskAssigneeIds(draft.value),
    set: (value: string[]) => Object.assign(draft.value, buildTaskAssigneePayload(value, props.assigneeOptions)),
});
const scheduleStartValue = computed(() => getTaskScheduleInputValues(draft.value).start);
const scheduleEndValue = computed(() => getTaskScheduleInputValues(draft.value).end);

function createDraft(): TaskQuickCreateDraft {
    return createTaskQuickDraft(props.initialDraft || {}, props.statusOptions[0] || 'Todo');
}

function resetDraft() {
    draft.value = createDraft();
    selectedTags.value = [];
}

function selectProject(projectId: string) {
    const previousProjectId = String(draft.value.projectId || '');
    draft.value.projectId = projectId || undefined;
    if (projectId !== previousProjectId) draft.value.milestoneId = undefined;
    const clientId = projectId ? String(projectOptionMap.value.get(projectId)?.clientId || '').trim() : '';
    if (clientId) draft.value.clientId = clientId;
}

function updateSchedule(startValue?: string, endValue?: string) {
    Object.assign(draft.value, normalizeTaskScheduleDates(startValue, endValue));
}

async function createTask() {
    if (!canCreate.value) return;
    saving.value = true;
    try {
        const taskData = buildQuickTaskPayload(draft.value, props.assigneeOptions);
        const createdBy = userStore.userId || authStore.user?.uid || 'unknown';
        const taskId = await taskStore.addTask(taskData, createdBy);
        try {
            await entityTagsStore.setEntityTags('task', taskId, selectedTags.value);
        } catch {
            toast.warning('Task created, but its tags could not be saved.');
        }
        const createdTask = await taskStore.fetchTaskById(taskId);
        if (!createdTask) throw new Error('Task was created but could not be reloaded.');
        toast.success('Task created.');
        emit('created', createdTask);
        emit('update:open', false);
        resetDraft();
    } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Task could not be created.');
    } finally {
        saving.value = false;
    }
}

watch(() => props.open, (open) => {
    if (open) {
        resetDraft();
        void entityTagsStore.fetchEntityTags();
    }
});

watch(() => props.initialDraft, () => {
    if (props.open) resetDraft();
}, { deep: true });
</script>

<template>
    <Sheet :open="open" @update:open="emit('update:open', $event)">
        <SheetContent side="right" class="w-full gap-0 p-0 sm:max-w-xl">
            <SheetHeader class="border-b px-6 py-5 text-left">
                <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    <ClipboardPlus class="size-4" />
                    Quick capture
                </div>
                <SheetTitle>New task</SheetTitle>
                <SheetDescription>Capture the work now. Open the task afterwards for recurrence, files, comments, or client response settings.</SheetDescription>
            </SheetHeader>

            <div class="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
                <div class="space-y-2">
                    <Label for="quick-task-title">Task title</Label>
                    <Input id="quick-task-title" v-model="draft.title" placeholder="What needs to be done?" autofocus @keydown.enter.prevent="createTask" />
                </div>

                <div class="space-y-2">
                    <Label for="quick-task-description">Description</Label>
                    <Textarea id="quick-task-description" v-model="draft.description" rows="4" placeholder="Add useful context (optional)" />
                </div>

                <div v-if="projectOptions.length" class="space-y-2">
                    <Label>Project</Label>
                    <SearchableSelectMenu
                        :model-value="String(draft.projectId || '')"
                        :options="projectMenuOptions"
                        placeholder="No linked project"
                        search-placeholder="Search projects..."
                        allow-clear
                        clear-label="No project"
                        show-all-by-default
                        variant="form"
                        @update:model-value="selectProject"
                    />
                </div>

                <TaskMilestoneSelect
                    v-model="draft.milestoneId"
                    :project-id="String(draft.projectId || '')"
                    :project-options="projectOptions"
                    description="Optional. This task will contribute to the selected milestone’s progress."
                    storage-key="quick-task-milestone"
                />

                <div v-if="clientOptions.length" class="space-y-2">
                    <Label>Client</Label>
                    <SearchableSelectMenu
                        v-model="draft.clientId"
                        :options="clientMenuOptions"
                        placeholder="No linked client"
                        search-placeholder="Search clients..."
                        allow-clear
                        clear-label="No client"
                        show-all-by-default
                        variant="form"
                    />
                </div>

                <div class="space-y-2">
                    <Label>Status</Label>
                    <SearchableSelectMenu v-model="draft.status" :options="statusMenuOptions" show-all-by-default variant="form" />
                </div>

                <div class="space-y-2">
                    <Label>Priority</Label>
                    <SearchableSelectMenu v-model="draft.priority" :options="priorityMenuOptions" show-all-by-default variant="form" />
                </div>

                <div v-if="assigneeOptions.length" class="space-y-2">
                    <Label>Assignees</Label>
                    <TaskAssigneePicker v-model="selectedAssigneeIds" :options="assigneeOptions" />
                </div>

                <div class="space-y-2">
                    <Label>Tags</Label>
                    <TagInput v-model="selectedTags" :suggestions="tagSuggestions" placeholder="For example Admin or Client review" />
                    <p class="text-xs text-muted-foreground">Tags stay internal to your workspace.</p>
                </div>

                <div class="space-y-3">
                    <div>
                        <Label>Schedule</Label>
                        <p class="mt-1 text-xs text-muted-foreground">Choose both dates to reserve a working range on the calendar. One date becomes the deadline.</p>
                    </div>
                    <div class="grid gap-4 sm:grid-cols-2">
                        <div class="min-w-0 space-y-2">
                            <Label>Start date</Label>
                            <DatePicker
                                :model-value="scheduleStartValue"
                                placeholder="No start date"
                                @update:model-value="value => updateSchedule(value, scheduleEndValue)"
                            />
                        </div>
                        <div class="min-w-0 space-y-2">
                            <Label>End / due date</Label>
                            <DatePicker
                                :model-value="scheduleEndValue"
                                placeholder="Optional"
                                @update:model-value="value => updateSchedule(scheduleStartValue, value)"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <SheetFooter class="sheet-safe-footer border-t px-6 pt-4">
                <Button type="button" variant="outline" :disabled="saving" @click="emit('update:open', false)">Cancel</Button>
                <Button type="button" :disabled="!canCreate" @click="createTask">{{ saving ? 'Creating...' : 'Create task' }}</Button>
            </SheetFooter>
        </SheetContent>
    </Sheet>
</template>
